import { useEffect, useState, useCallback } from "react";
import { CardVariant, SummaryCard } from "components/ui/summary-card";
import {
    fetchCnAnalysisSummary,
    fetchCnAnalysisData,
    type CnAnalysisSummaryResponse,
    type CnRow,
    type CnAnalysisDataRequest,
    CnFilterType,
    BranchCnResponse,
    fetchBranchCns,
    type BranchCnRow
} from "./api/reports";

import { useBreadcrumbs } from "../../contexts/BreadcrumbsContext";
import { DataTable } from "components/ui/data-table";
import { PaginationState } from "@tanstack/react-table";
import { createCnColumns } from "./CnAnalysisColumns";
import { createBranchCnColumns } from "./BranchCnColumns";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Button } from "components/ui/button";
import { X, CalendarIcon } from "lucide-react";
import { Calendar } from "components/ui/calendar";
import { cn, toIST } from "lib/utils";
import { format } from "date-fns";

const CnAnalysisPage = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        setBreadcrumbs([
            { label: "Reports", href: "/reports" },
            { label: "CN Analysis" }
        ]);
    }, [setBreadcrumbs]);

    const [summary, setSummary] = useState<CnAnalysisSummaryResponse | null>(null);
    const [tableData, setTableData] = useState<CnRow[]>([]);
    const [branchData, setBranchData] = useState<BranchCnResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [activeFilter, setActiveFilter] = useState<CnFilterType>("total");

    const [pagination, setPagination] = useState<PaginationState>({pageIndex: 0, pageSize: 20});
    const [pageCount, setPageCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([
        { id: "branch_name", desc: false }
    ]);
    const [branchSorting, setBranchSorting] = useState<{ id: string; desc: boolean}[]>([
        {id: "cn_number", desc: false}
    ]);

    const [date, setDate] = useState<{ from: Date | null; to: Date | null }>({
        from: null,
        to: null,
    });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [tempFromDate, setTempFromDate] = useState<Date | null>(null);
    const [activeQuickFilter, setActiveQuickFilter] = useState<string>("");

    const [isBranchView, setIsBranchView] = useState(false);

    const [branchOuCode, setBranchOuCode] = useState<string | null>(null);
    const [branchName, setBranchName] = useState<string | null>(null);
    const [branchPagination, setBranchPagination] = useState<PaginationState>({pageIndex: 0, pageSize: 20});

    const loadBranchCns = useCallback((ou_code: string, branch_name: string) => {
        setIsBranchView(true);
        setBranchOuCode(ou_code);
        setBranchName(branch_name);
        setBranchPagination({ pageIndex: 0, pageSize: 20 });
    }, []);

    const fetchBranchList = useCallback(async () => {
        if (!branchOuCode) return;

        try {
            const sortId = branchSorting[0]?.id || null;
            const sortOrder = branchSorting[0]?.desc ? "desc" : "asc";

            setLoading(true);

            const response = await fetchBranchCns({
                ou_code: branchOuCode,
                filter: activeFilter,
                page: branchPagination.pageIndex + 1,
                page_size: branchPagination.pageSize,
                sort_by: sortId,
                sort_order: sortOrder,
                dateFrom: date.from ? toIST(date.from) : null,
                dateTo: date.to ? toIST(date.to) : null,
            });

            setBranchData(response);
        } finally {
            setLoading(false);
        }
    }, [branchOuCode, branchPagination, branchSorting, date, activeFilter]);

    useEffect(() => {
        if (isBranchView && branchOuCode) {
            fetchBranchList();
        }
    }, [isBranchView, branchOuCode, branchPagination, branchSorting, fetchBranchList]);

    const handleDateSelect = (selected: Date | undefined) => {
        if (!selected) return;

        if (!tempFromDate) {
            setTempFromDate(selected);
        } else {
            setDate({ from: tempFromDate, to: selected });
            setActiveQuickFilter("Custom");
            setTempFromDate(null);
            setIsDatePickerOpen(false);

            setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
            setBranchPagination({ pageIndex: 0, pageSize: branchPagination.pageSize });
        }
    };

    const handleQuickFilter = (label: string, days: number) => {
        setActiveQuickFilter(label);

        const to = new Date();
        const from = new Date();
        from.setDate(to.getDate() - (days - 1));

        setDate({ from, to });

        setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
        setBranchPagination({ pageIndex: 0, pageSize: branchPagination.pageSize });

        setTempFromDate(null);
        setIsDatePickerOpen(false);
    };

    const columns = createCnColumns(loadBranchCns);

    useEffect(() => {
        if (!isBranchView) {
            fetchCnAnalysisSummary({
                dateFrom: date.from ? toIST(date.from) : null,
                dateTo: date.to ? toIST(date.to) : null,
            }).then(setSummary);
        }
    }, [date.to, isBranchView]);

    const fetchTable = useCallback(async () => {
        if (isBranchView) return;
        try {
            setLoading(true);

            const sortId = sorting[0]?.id || null;

            const payload: CnAnalysisDataRequest = {
                filter: activeFilter,
                page: pagination.pageIndex + 1,
                page_size: pagination.pageSize,
                sort_by: sortId,
                sort_order: sorting[0]?.desc ? "desc" : "asc",
                dateFrom: date.from ? toIST(date.from) : null,
                dateTo: date.to ? toIST(date.to) : null,
            };

            const res = await fetchCnAnalysisData(payload);

            setTableData(res.rows ?? []);
            setTotalItems(res.total_items ?? 0);
            setPageCount(res.total_pages ?? 0);

        } finally {
            setLoading(false);
        }
    }, [activeFilter, pagination, sorting, date, isBranchView]);

    useEffect(() => {
        if (!isBranchView) {
            fetchTable();
        }
    }, [fetchTable, isBranchView]);


    const handleCardClick = (filter: CnFilterType) => {
        setActiveFilter(filter);
        setIsBranchView(false);
        setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    };

    if (!summary) {
        return <div className="p-4 text-sm text-slate-600">Loading summary...</div>;
    }

    const summaryList = [
        {
            key: "total",
            title: summary.total.label,
            count: summary.total.count,
            cn_value: summary.total.cn_value,
            actual_weight: summary.total.actual_weight,
            charge_weight: summary.total.charge_weight,
            variant: "total",
        },
        {
            key: "bank",
            title: summary.bank_cns.label,
            count: summary.bank_cns.count,
            cn_value: summary.bank_cns.cn_value,
            actual_weight: summary.bank_cns.actual_weight,
            charge_weight: summary.bank_cns.charge_weight,
            variant: "positive",
        },
        {
            key: "no_ewaybill",
            title: summary.no_ewaybill.label,
            count: summary.no_ewaybill.count,
            cn_value: summary.no_ewaybill.cn_value,
            actual_weight: summary.no_ewaybill.actual_weight,
            charge_weight: summary.no_ewaybill.charge_weight,
            variant: "negative",
        },
    ];

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-3">
                {summaryList.map((item) => {
                    const borderColor = {
                        total: "border-blue-500",
                        positive: "border-emerald-500",
                        negative: "border-red-500",
                    }[item.variant as "total" | "positive" | "negative"];

                    const isActive = activeFilter === item.key;

                    return (
                        <SummaryCard
                            key={item.key}
                            title={item.title}
                            count={item.count}
                            cn_value={item.cn_value}
                            actual_weight={item.actual_weight}
                            charge_weight={item.charge_weight}
                            variant={item.variant as CardVariant}
                            onClick={() => handleCardClick(item.key as CnFilterType)}
                            className={`cursor-pointer transition ${isActive ? `border ${borderColor}` : ""}`}
                        />
                    );
                })}
            </div>

            <div className="px-3 flex justify-between items-center">
                <div className="flex items-center border rounded-md bg-background w-fit">
                    <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                        <div className="flex divide-x">
                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    onClick={() => setIsDatePickerOpen(true)}
                                    className={cn(
                                        "h-7 px-2 py-1 justify-start text-left font-normal rounded-none rounded-l-md hover:bg-muted/50 text-xs",
                                        !date?.from && "text-gray-500"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-3 w-3" />
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-xs text-gray-600">From Date</span>
                                        {date?.from && <span className="text-xs truncate">{format(date.from, "dd MMM yyyy")}</span>}
                                    </div>
                                </Button>
                            </PopoverTrigger>

                            <PopoverTrigger asChild>
                                <Button
                                    variant="ghost"
                                    onClick={() => setIsDatePickerOpen(true)}
                                    className={cn(
                                        "h-7 px-2 py-1 justify-start text-left font-normal rounded-none rounded-r-md hover:bg-muted/50 text-xs",
                                        !date?.to && "text-gray-500"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-3 w-3" />
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-xs text-gray-600">To Date</span>
                                        {date?.to && <span className="text-xs truncate">{format(date.to, "dd MMM yyyy")}</span>}
                                    </div>
                                </Button>
                            </PopoverTrigger>
                        </div>

                        <PopoverContent className="w-auto p-0" align="start">
                            <div className="p-4">
                                <div className="text-sm font-semibold mb-3 text-gray-900">
                                    {!tempFromDate ? "Select start date" : "Select end date"}
                                </div>

                                <Calendar
                                    mode="single"
                                    selected={(tempFromDate ?? date?.from) || undefined}
                                    onSelect={handleDateSelect}
                                    numberOfMonths={2}
                                    disabled={(d) => {
                                        if (tempFromDate) {
                                            return d < tempFromDate;
                                        }

                                        return false;
                                    }}
                                />

                                <div className="flex justify-end mt-4 pt-3 border-t">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setIsDatePickerOpen(false);
                                            setTempFromDate(null);
                                        }}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="flex gap-2">
                    {[
                        { label: "Today", days: 1 },
                        { label: "Last 7 Days", days: 7 },
                        { label: "Last 30 Days", days: 30 },
                        { label: "Last 90 Days", days: 90 },
                    ].map((btn) => {
                        const isActive = activeQuickFilter === btn.label;
                        return (
                            <Button
                                key={btn.label}
                                onClick={() => handleQuickFilter(btn.label, btn.days)}
                                size="sm"
                                className={`
                                    h-8 px-3 text-xs font-medium rounded-md
                                    border transition
                                    ${isActive
                                        ? "bg-gray-800 text-white shadow-sm hover:bg-black hover:text-gray-50"
                                        : "bg-gray-100 text-black hover:bg-gray-200"}
                                `}
                                variant="ghost"
                            >
                                {btn.label}
                            </Button>
                        );
                    })}
                    {
                    date.from && (
                        <Button variant="ghost" onClick={() => {
                            setDate({ from: null, to: null });
                            setActiveQuickFilter("");
                            setTempFromDate(null);
                            setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
                            setBranchPagination({ pageIndex: 0, pageSize: branchPagination.pageSize });
                        }} className="h-8 px-3">
                            Reset filter
                            <X className="ml-2 h-4 w-4" />
                        </Button>
                    )
                }
                </div>
            </div>

            {isBranchView && (
                <div className="flex items-center gap-4 mb-4">
                    <button
                        className="px-3 py-1 font-bold text-sm"
                        onClick={() => {
                            setIsBranchView(false);
                            setBranchData(null);
                        }}
                    >
                        ←
                    </button>

                    <h2 className="text-lg font-semibold">Branch: {branchName}</h2>
                </div>
            )}

            <div className="mt-6">
                {isBranchView ? (
                    <DataTable<BranchCnRow, unknown>
                        columns={createBranchCnColumns()}
                        data={branchData?.rows || []}
                        loading={loading}
                        pagination={branchPagination}
                        onPaginationChange={setBranchPagination}
                        sorting={branchSorting}
                        onSortingChange={setBranchSorting}
                        hideToolbar
                        totalCount={branchData?.total_items || 0}
                        pageCount={branchData?.total_pages || 0}
                    />
                ) : (
                    <DataTable<CnRow, unknown>
                        columns={columns}
                        data={tableData}
                        loading={loading}
                        pagination={pagination}
                        onPaginationChange={setPagination}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        hideToolbar
                        totalCount={totalItems}
                        pageCount={pageCount}
                    />
                )}
            </div>
        </div>
    );
};

export default CnAnalysisPage;
